import { Settings } from "luxon";

import UserApi from "@/api/userApi";
import Notifications from "@/stores/modules/user/NotificationsModule";
import Organization from "@/stores/modules/OrganizationModule";
import router from "@/router";
import { DEFAULT_LANG, getLanguages } from "@/utility/LocalizationService";
import { evalRequireRoles } from "@/router/helpers/routerHelper";
import * as Sentry from "@sentry/vue";
import { enableSentry } from "@/common/config";

export default {
  state: {
    lang: undefined,
    user: {},
    authenticated: localStorage.token ? true : false,
    machineGroups: [],
    roles: [],
    selectedGroup: {},
    changeGroup: false,
    mode: "",
    countSidebarMenuNavItems: 0,
    sideMenuIsOpen: false,
    machineGroupDisabled: false
  },
  getters: {
    getCountSidebarMenuNavItems: state => state.countSidebarMenuNavItems,
    getSideMenuIsOpen: state => state.sideMenuIsOpen,
    getUser(state) {
      return state.user;
    },
    getLang(state) {
      return state.lang;
    },
    isAuthenticated(state) {
      return state.authenticated;
    },
    getRoles(state) {
      return state.roles;
    },
    getMachineGroups: state => state.machineGroups,
    getOrganizationCode: state => state.user.OrganizationCode || localStorage.organizationCode,
    getSelectedGroup: state => state.selectedGroup,
    getChangingGroup: state => state.changeGroup,
    getMode: state => state.mode,
    isCustomer: state => state.user.OrganizationType === "CUSTOMER",
    machineGroupDisabled: state => state.machineGroupDisabled
  },
  actions: {
    async login({ dispatch }, args) {
      const api = new UserApi("");
      let user;
      if (!args.isLoginWithEmail) {
        user = await api.login(args.username, args.password, args.isLoginWithEmail);
      } else {
        user = await api.loginWithEmail(args.loginEmail, args.password, args.isLoginWithEmail);
      }
      if (!!user && user.Id) {
        // -- IMPORTANT
        localStorage.setItem("organizationCode", user.OrganizationCode);
        localStorage.setItem("userId", user.Id);
        // --
        dispatch("initUser", user);
      }
    },
    async loginMe({ dispatch }) {
      dispatch("Alert/Errors/resetVal", null, { root: true });
      const api = new UserApi(localStorage.organizationCode);
      try {
        const user = await api.getMe();

        dispatch("initUser", user);
        // Init mode
        if (localStorage.mode) {
          dispatch("setMode", localStorage.mode);
        }
      } catch (e) {
        window.location.pathname !== "/login" && router.push({ name: "login" }).catch(() => {});
      }
    },

    async initUser({ commit, dispatch, rootGetters }, user) {
      if (enableSentry) {
        Sentry.setUser({ id: user.Id, email: user.Email });
      }

      // set user
      gtag("set", "user_properties", {
        user_id: user.Id,
        organization: user.OrganizationCode,
        email: user.Email,
        name: user.Firstname,
        last_name: user.Lastname
      });

      // tracking login event
      gtag("event", "login", {
        method: user.loginMethod || "Local login"
      });

      // Set Oranization
      if (
        !rootGetters["Organization/selected"] ||
        (rootGetters["Organization/selected"] &&
          rootGetters["Organization/selected"].OrganizationCode !== user.OrganizationCode)
      ) {
        dispatch("Organization/selectOrganization", user.OrganizationCode);
      }

      commit("LOGIN", user);
      commit("AUTHENTICATE");
      commit("UPD_LANG", user.Language);

      // INIT GROUPS
      commit("SET_GROUPS", user.Machine_Group_Ids || []);

      // SELECT GROUP
      let selectGroup = null;
      if (user.Machine_Group_Ids.length) {
        selectGroup = user.Machine_Group_Ids.find(
          group => group.Id === localStorage.selectedGroupId
        );
        if (!selectGroup) {
          // Group All
          const groupAll = user.Machine_Group_Ids.find(group => group.Name.toLowerCase() === "all");
          if (groupAll) {
            selectGroup = groupAll;
          }
          // First group
          else {
            selectGroup = user.Machine_Group_Ids[0];
          }
        }
      }

      await dispatch("setSelectedGroup", selectGroup);

      // Init count notifications
      await dispatch("Notifications/setUpdateCount", new Date());

      // Check Roles Access
      await dispatch("checkRolesAllowedOnPage", user);
    },
    async logout({ getters, commit, dispatch }) {
      const api = new UserApi(getters["getUser"].OrganizationCode);
      await api.logout();

      commit("LOGOUT");
      commit("RESET_GROUP", []);
      dispatch("Notifications/resetNotifications");
      dispatch("Machine/resetAll", null, { root: true });

      if (enableSentry) {
        Sentry.configureScope(scope => scope.setUser(null));
      }
    },
    async resetPassword(_, args) {
      const api = new UserApi("");
      const param = { Email: args };
      await api.resetPassword(param);
    },

    async changePassword({ getters }, args) {
      const api = new UserApi(getters["getUser"].OrganizationCode);
      const param = {
        password: args,
        confirmPassword: args
      };
      await api.resetPassword(param);
    },

    authenticate: ({ commit }) => {
      commit("AUTHENTICATE");
    },
    async setSelectedGroup({ commit }, args) {
      commit("CHANGE_GROUP", true);
      if (args) {
        commit("SELECTED_GROUP", args);
        typeof args.Id === "undefined" || localStorage.setItem("selectedGroupId", args.Id);
        commit("CHANGE_GROUP", false);
      }
    },

    setMode({ state }, args) {
      state.mode = localStorage.mode = args;
    },

    async updateLang({ state, commit }, args) {
      const api = new UserApi(state.user.OrganizationCode);
      await api.updateUser(state.user.Id, args);
      state.user.Language = args.Language;
      commit("UPD_LANG", args.Language);
    },

    checkRolesAllowedOnPage({ state }, args) {
      const user = args ? args : state.user;
      if (!!router && (!user.IsAdmin || router.currentRoute.meta.forceRequiresRoleOverIsAdmin)) {
        if (!evalRequireRoles(router.currentRoute, user.Role_Ids)) {
          console.log("Role denied");
          router.push({ name: "access_denied" });
        }
      }
    },

    setCountSidebarMenuNavItems({ commit }, args) {
      commit("SET_COUNT_SIDE_NAV_ITEMS", args);
    },
    setSideMenuIsOpen({ commit }, args) {
      commit("SET_SIDE_MENU_IS_OPEN", args);
    },

    setMachineGroupDisabled({ commit }, args) {
      commit("SET_MACHINEGROUP_DISABLED", args);
    },

    async setMachineGroupAll({ state, dispatch }) {
      localStorage.setItem("selectedGroupId", "");
      const groupAll = state.machineGroups.find(group => group.Name.toLowerCase() === "all");
      await dispatch("setSelectedGroup", groupAll);
    },

    async updateUserSubscriptions({ state }, { data }) {
      const user = state.user;
      const userApi = new UserApi(user.OrganizationCode);

      try {
        if (data.subscriptions) {
          await userApi.updateUserSubscriptions(data.subscriptions);
        }
        if (data.channels) {
          await userApi.updateUserSubscriptionsChannels(data.channels);
        }
        return true;
      } catch (e) {
        return false;
      }
    },

    async getUserSubscriptionsChannels({ state }) {
      const userApi = new UserApi(state.user.OrganizationCode);
      const result = await userApi.getUserSubscriptionsChannels();
      return result;
    },

    async getUserSubscriptions({ state }) {
      const userApi = new UserApi(state.user.OrganizationCode);
      const result = await userApi.getUserSubscriptions();
      return result;
    },

    async updateUserFirstLogin({ state }, args) {
      const api = new UserApi(state.user.OrganizationCode);
      return await api.updateUser(state.user.Id, { FirstLogin: args });
    }
  },
  mutations: {
    AUTHENTICATE: state => {
      state.authenticated = true;
    },
    UPD_LANG: (state, payload) => {
      localStorage.setItem("language", payload);
      Settings.defaultLocale = localStorage.language.substring(0, 2);

      const findUserLanguage = getLanguages().find(l => l.id === payload);
      if (findUserLanguage) {
        state.lang = payload;
      } else {
        state.lang = DEFAULT_LANG;
      }
    },
    LOGIN: (state, user) => {
      state.user = user;
      state.roles = user.Role_Ids;
    },
    LOGOUT: state => {
      state.authenticated = false;
      state.user = {};
      state.selectedGroup = {};
      state.roles = [];
      state.machineGroups = [];
      state.mode = "";
      state.countSidebarMenuNavItems = 0;
      state.machineGroupDisabled = false;
    },
    RESET_GROUP: state => {
      state.machineGroups = [];
    },
    SET_GROUPS: (state, payload) => {
      state.machineGroups = payload;
    },
    SELECTED_GROUP: (state, item) => {
      state.selectedGroup = item;
    },
    CHANGE_GROUP: (state, payload) => {
      state.changeGroup = payload;
    },
    SET_COUNT_SIDE_NAV_ITEMS: (state, payload) => {
      state.countSidebarMenuNavItems = payload;
    },
    SET_SIDE_MENU_IS_OPEN: (state, payload) => {
      state.sideMenuIsOpen = payload;
    },
    SET_MACHINEGROUP_DISABLED: (state, payload) => {
      state.machineGroupDisabled = payload;
    }
  },
  modules: {
    Notifications,
    Organization
  },
  namespaced: true
};
